import * as React from "react";

import { Form, FormikProps } from "formik";
import { VALUABLE_ABOUT, VALUABLE_PRICE } from "../valuablesConstants";
import {
  ProductIntroFormValues,
  ProductPriceFormValue,
  ValuablesPriceRequestInfo,
  ValuablesText,
} from "../valuablesInterface";

import { ErrorBoundary } from "@sentry/react";
import { MarketingConsent } from "Components/Consent/MarketingConsent";
import { ProductPriceTile } from "Components/Features/CheckPrice/Tiles/ProductPriceTile";
import { SystemErrorAlert } from "Components/Form/Alert/SystemErrorAlert";
import { ErrorsSummedUp } from "Components/Form/ErrorsSummedUp";
import { NavigationButtons } from "Components/NavigationButtons";
import { ScrollToFirstError } from "Components/ScrollToFirstError";
import { useTexts } from "Components/TextContext";
import { ProductBackendName } from "Features/CheckPrice/Products/productTypes";
import { useAddProductToCartHandler } from "Store/addProductToCart/hooks";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { ValuablesCoverage } from "Store/interfaces/products/coverage";
import { useFormState } from "Store/priceCalculation/priceCalculationHooks";
import { ReduxState } from "Store/rootReducer";
import { TextLanguage } from "Store/texts/interfaces";
import { getDebounce } from "Utils/debounce";
import { mapValidationErrors } from "Utils/formUtils";
import { isDefined } from "Utils/functions";
import { useSelector } from "react-redux";
import { extractLabels } from "../valuablesUtils/extractLabels";
import { mapFormToPriceRequest } from "../valuablesUtils/mapFormToPriceRequest";
import { getCoverageAmountSchema as validationSchema } from "../valuablesValidation";
import { ValuablesCoveragePrice } from "./CoveragePrice";
import { ValuablesProductPriceFormElements } from "./FormElements";

const debounce = getDebounce(500);

interface Props {
  sendPriceRequest: (
    payload: ValuablesPriceRequestInfo,
    stepForwardOnSuccess?: boolean,
  ) => void;
  texts: ValuablesText;
  lang: TextLanguage;
}

export const ValuablesProductPrice = (props: Props): React.ReactElement => {
  const { sendPriceRequest, texts, lang } = props;
  const { getText } = useTexts();

  const introFormValues = useFormState(
    VALUABLE_ABOUT.id,
  ) as ProductIntroFormValues;
  const addProductToCart = useAddProductToCartHandler(VALUABLE_PRICE.id);

  const submitHandler = (values: any): void => {
    const productToAdd = {
      coverage: ValuablesCoverage.VALUABLES,
      productType: INSURANCE_NAME_VALUE.VALUABLES,
    };

    const requestInfo = {
      ...introFormValues,
      ...values,
    };

    addProductToCart(requestInfo, [productToAdd]);
  };

  const updatePrices = (coverageAmount: number | undefined): void => {
    const formValues = {
      ...introFormValues,
      coverage_amount: coverageAmount,
    };

    const isValid = validationSchema(getText).isValidSync(formValues);

    if (!isValid) {
      return;
    }

    debounce(sendPriceRequest, mapFormToPriceRequest(formValues), false);
  };

  const {
    addProductToCart: submitState,
    priceCalculation: { priceCalculationResult },
  } = useSelector((state: ReduxState) => state);

  return (
    <ErrorBoundary
      beforeCapture={(scope) =>
        scope.setTag(
          "location",
          `CheckPrice - ProductPrice - ${ProductBackendName.VALUABLES}`,
        )
      }
    >
      <ProductPriceTile
        header={getText("pageheader.valuables.checkprice")}
        onSubmit={submitHandler}
        validationSchema={validationSchema(getText)}
        initialValues={introFormValues}
      >
        {(formProps: FormikProps<ProductPriceFormValue>) => (
          <Form>
            <ValuablesProductPriceFormElements
              formProps={formProps}
              updatePrices={updatePrices}
            />

            <ValuablesCoveragePrice texts={texts} lang={lang} />

            {(isDefined(submitState.error) ||
              isDefined(priceCalculationResult.error)) && (
              <SystemErrorAlert alertId="priceCalculation" />
            )}

            <ErrorsSummedUp
              errors={mapValidationErrors(
                formProps.errors,
                extractLabels(texts, introFormValues.product),
                lang,
              )}
              isVisible
            />
            <MarketingConsent />
            <ScrollToFirstError />
            <NavigationButtons
              isUpsell
              isLoading={
                submitState.isLoading || priceCalculationResult.isLoading
              }
            />
          </Form>
        )}
      </ProductPriceTile>
    </ErrorBoundary>
  );
};
